import type { DocumentNode } from "@apollo/client/core"
import { useApolloClient } from "@vue/apollo-composable"
import { onScopeDispose, ref, type Ref } from "vue"
import type { PageFragment } from "../../PageFragment"
import { notifyError } from "../../notifications"

export interface UseAsyncEntitySearchOptions<TResult, TItem extends { uuid: string }> {
  queryDocument: DocumentNode
  extract: (result: TResult) => PageFragment<TItem>
  pageSize?: number
  debounceMs?: number
  /**
   * Extra variables merged into every query call (after `query`/`page`/`limit`).
   * Useful for passing a fixed `order` or other operation-specific filters
   * that don't depend on the search input.
   */
  extraVariables?: Record<string, unknown>
}

export interface UseAsyncEntitySearchApi<TItem> {
  items: Readonly<Ref<TItem[]>>
  loading: Readonly<Ref<boolean>>
  hasMore: Readonly<Ref<boolean>>
  search: (query: string) => void
  loadMore: () => void
  reset: () => void
}

interface Variables extends Record<string, unknown> {
  query: string
  page: number
  limit: number
}

/**
 * Async entity search composable for Apollo-backed combobox inputs.
 *
 * Encapsulates the common pattern of: debounced search-with-reset, incremental
 * `loadMore` pagination, stale-response discard (via a monotonic sequence
 * counter), and error notification. The composable is imperative — call
 * `search()`, `loadMore()`, `reset()` — and exposes reactive `items`,
 * `loading`, and `hasMore` refs for the consuming component.
 *
 * Implementation note: we use `useApolloClient().client.query()` directly
 * rather than `useLazyQuery` because the latter's reactive `variables` watch
 * would cause a double-fire when the imperative API updates variables and
 * also calls `refetch()`. The imperative client API fits the imperative
 * composable API naturally.
 */
export function useAsyncEntitySearch<TResult, TItem extends { uuid: string }>(
  options: UseAsyncEntitySearchOptions<TResult, TItem>
): UseAsyncEntitySearchApi<TItem> {
  const pageSize = options.pageSize ?? 20
  const debounceMs = options.debounceMs ?? 300

  const items = ref<TItem[]>([]) as Ref<TItem[]>
  const loading = ref(false)
  const hasMore = ref(false)

  let currentQuery = ""
  let currentPage = 0
  let sequence = 0
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const { client } = useApolloClient()

  function runFetch(query: string, page: number, mode: "replace" | "append", seq: number): void {
    const variables: Variables = { query, page, limit: pageSize, ...options.extraVariables }
    client
      .query<TResult, Variables>({
        query: options.queryDocument,
        variables,
        fetchPolicy: "no-cache"
      })
      .then((result) => {
        if (seq !== sequence) return  // stale — discard
        // The Apollo client is configured with errorPolicy: "all" globally,
        // so GraphQL errors come back in `result.errors` rather than as a
        // rejection. Surface them through notifyError so the user sees the
        // failure (otherwise the dropdown silently shows an empty list).
        if (result.errors && result.errors.length > 0) {
          const message = result.errors.map((e) => e.message).join("; ")
          notifyError(new Error(message))
          loading.value = false
          return
        }
        const data = result.data
        if (!data) {
          loading.value = false
          return
        }
        const fragment = options.extract(data)
        if (mode === "replace") {
          items.value = [...fragment.content]
        } else {
          items.value = [...items.value, ...fragment.content]
        }
        hasMore.value = fragment.hasNext
        loading.value = false
      })
      .catch((err: Error) => {
        if (seq !== sequence) return
        notifyError(err)
        loading.value = false
      })
  }

  function search(query: string): void {
    // Begin a new request session immediately so any in-flight response and
    // any pending debounce timer are invalidated.
    sequence += 1
    const seq = sequence
    currentQuery = query
    currentPage = 0
    loading.value = true

    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      if (seq !== sequence) return  // superseded by another search()
      runFetch(currentQuery, 0, "replace", seq)
    }, debounceMs)
  }

  function loadMore(): void {
    if (loading.value) return
    if (!hasMore.value) return
    sequence += 1
    const seq = sequence
    currentPage += 1
    loading.value = true
    runFetch(currentQuery, currentPage, "append", seq)
  }

  function reset(): void {
    sequence += 1  // invalidate any in-flight response
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    items.value = []
    loading.value = false
    hasMore.value = false
    currentQuery = ""
    currentPage = 0
  }

  onScopeDispose(() => {
    sequence += 1
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  })

  return { items, loading, hasMore, search, loadMore, reset }
}
